<template>
  <billing-dialog v-model="visible" class="opc-sdk-uc-detail-dialog" show-close @close="handleCancel" @back-event="visible = false">
    <!--header-->
    <img v-if="logo" :src="logo" alt="" class="opc-plan-detail-dialog__logo">
    <h1 class="opc-sdk-uc-detail-dialog__title">{{ $t('billing.video_sdk_uc.title') }}</h1>
    <div class="opc-sdk-uc-detail-dialog__price">
      <Price :currency="currencySymbol" :price="leftCreditPlan.price" :unit="leftCreditPlan.unit"
             class="opc-sdk-uc-detail-dialog__price-item" />
      <div class="opc-sdk-uc-detail-dialog__price-save-item">
        <Price :currency="currencySymbol" :price="rightCreditPlan.price" :unit="rightCreditPlan.unit"
               class="opc-sdk-uc-detail-dialog__price-item"/>
      </div>
    </div>
    <h2 class="opc-sdk-uc-detail-dialog__content-title">{{ $t('billing.video_sdk_uc.feature_title') }}</h2>
    <div v-for="item in featureList" :key="item.title" class="opc-sdk-uc-detail-dialog__feature-item">
      <div class="opc-sdk-uc-detail-dialog__feature-row">
        <img alt="" src="../../../assets/plans/check.svg">
        <h2 class="opc-sdk-uc-detail-dialog__feature-title">{{ item.title }}</h2>
      </div>
      <p class="opc-sdk-uc-detail-dialog__feature-desc">{{ item.tips }}</p>
    </div>
  </billing-dialog>
</template>

<script type="text/jsx" lang="jsx">
import _ from 'lodash';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import Price from '@/components/price';
import { getCurrency, getCurrencySymbol } from '@/utils/currency';
import { planTypes } from '@/common/constants';
import { mapGetters } from 'vuex';
import { plansof } from '@/utils/chain';
import BillingDialog from '@/components/billing-dialog.vue';
import { toThousands } from '@/utils/money';

export default {
  name: 'PlanDetails',
  mixins: [ConfirmComponent],
  components: {
    Price,
    BillingDialog,
  },
  data() {
    return {
      visible: false,
      logo: '',
      currencySymbol: getCurrencySymbol(getCurrency()),
      title: '',
    };
  },
  methods: {
    handleSure() {
      this.$tracking.buyInteraction('product details', 'plan details - see full feature list');
    },
  },
  computed: {
    ...mapGetters(['planMapFlat','enableSDKFreeTrial']),
    isEnableFTSDK() {
      return !!this.enableSDKFreeTrial
    },
    orderPlanList() {
      return _.orderBy(plansof(this.planMapFlat).type(planTypes.VIDEO_SDK_UC).get(), 'credit');
    },
    leftCreditPlan() {
      const plan = _.get(this.orderPlanList, [0]) || {};

      return {
        unit: this.$t('billing.video_sdk_uc.credits_unit', [plan.orginalCredit || plan.credit]),
        price: this.$money(plan.amount, { forceDiff: true }),
      };
    },
    rightCreditPlan() {
      const plan = _.findLast(this.orderPlanList) || {};

      return {
        unit: this.$t('billing.video_sdk_uc.credits_unit', [plan.credit]),
        price: this.$money(plan?.amount, { forceDiff: true }),
      };
    },
    featureList() {
      const lastPlan = _.findLast(this.orderPlanList) || {};

      return [
      {
        title: this.$t('billing.video_sdk.plan_details.cloud_based'),
        tips: this.$t('billing.video_sdk.plan_details.cloud_based_desc'),
      }, {
        title: this.$t('billing.video_sdk.plan_details.customizable'),
        tips: this.$t('billing.video_sdk.plan_details.customizable_desc',[toThousands(1000)]),
      }, {
        title: this.$t('billing.video_sdk.plan_details.hd'),
        tips: this.$t('billing.video_sdk.plan_details.hd_desc') + '\n' + this.$t('billing.video_sdk.plan_details.hd_desc_audio'),
      }, {
        title: this.$t('billing.video_sdk_uc.feature_bundle.title'),
        tips: this.$t('billing.video_sdk_uc.feature_bundle.desc'),
      }, {
        title: this.$t('billing.video_sdk_uc.feature_flexible.title'),
        tips: this.$t('billing.video_sdk_uc.feature_flexible.desc'),
      }, {
        title: this.$t('billing.video_sdk_uc.feature_recommend.title', [lastPlan.credit]),
        tips: this.$t('billing.video_sdk_uc.feature_recommend.desc'),
      }, {
        title: this.$t('billing.video_sdk_uc.feature_for_future.title'),
        tips: this.$t('billing.video_sdk_uc.feature_for_future.desc'),
      }, {
        title: this.$t('billing.video_sdk_uc.feature_cobrowsing.title'),
        tips: this.$t('billing.video_sdk_uc.feature_cobrowsing.desc'),
      }]
    },
  },
  watch: {
    visible(val) {
      this.$tracking.buyInteraction(
        'product details',
        `SDK Universal Credit  -  ${val ? 'impression' : 'close'}`,
      );
    },
  },
};

</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.opc-sdk-uc-detail-dialog {
  .zbo-dialog {
    min-width: 522px;
    padding: 70px 60px;
    width: fit-content;
  }

  &__title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 33px */
    margin-bottom: 16px;
  }

  &__price {
    width: 100%;
    display: flex;
    flex-direction: row;
    margin-bottom: 24px;
    align-items: flex-end;

    p.price.price-component {
      flex-wrap: wrap;
      margin-right: 10px;
    }
  }

  &__price-save-item {
    border-left: 2px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
    padding-left: 24px;
    flex: 1;
  }

  &__content-title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 21px */
    margin-bottom: 8px;
  }

  &__feature-item {
    margin-bottom: 11px;
  }

  &__feature-row {
    display: flex;

    img {
      margin-top: 4px;
      margin-right: 8px;
    }
  }

  &__feature-title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 21px */
  }

  &__feature-desc {
    color: var(--zoom-color-text-strong-neutral, #555B62);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    padding-left: 20px;
    white-space: pre-line;
  }

  .opc-price-save-count {
    border-radius: 4px 8px !important;
    background: var(--Saving, linear-gradient(93deg, #268543 7.27%, #1C837E 92.73%));
  }
  &__disclaimer{
    color: var(--zoom-color-text-strong-neutral, #555B62);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    margin-top: 16px;
    width: 100%;
    max-width: 760px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  p.price.price-component.price--no-affix {
    max-width: 100%;
  }
}

@media (min-width: 721px) and (max-width: 1024px) {
  .opc-sdk-uc-detail-dialog {
    &__price-save-item {
      flex: 1;
    }
  }
}
</style>
